// One persistent toast region for the whole dashboard. Replaces the
// per-call-site `.toast-zone > .toast` markup (ChecklistTemplatesPanel,
// ClassifyFindingModal, DetalleTienda, Observaciones — still hand-rolled,
// migration is a follow-up).
//
// The <div aria-live> is always in the DOM, even when empty: screen
// readers only announce insertions into a live region that already
// existed when the page settled. Timing: visual fade at `durationMs`
// (3200 by default, `.toast--leaving`), DOM removal ~1300ms later so the
// announcement is not cut short.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { ToastContext } from "../lib/useToast";
import type { ToastApi, ToastItem, ToastTone } from "../lib/useToast";

const DEFAULT_DURATION_MS = 3200;
const REMOVE_AFTER_FADE_MS = 1300;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>[]>());

  const clearTimers = useCallback((id: number) => {
    for (const t of timers.current.get(id) ?? []) clearTimeout(t);
    timers.current.delete(id);
  }, []);

  const dismiss = useCallback<ToastApi["dismiss"]>(
    (id) => {
      if (id === undefined) {
        for (const key of Array.from(timers.current.keys())) clearTimers(key);
        setToasts([]);
        return;
      }
      clearTimers(id);
      setToasts((list) => list.filter((t) => t.id !== id));
    },
    [clearTimers]
  );

  const show = useCallback<ToastApi["show"]>((message, tone: ToastTone = "ok", opts) => {
    const id = nextId.current++;
    const duration = opts?.durationMs ?? DEFAULT_DURATION_MS;
    setToasts((list) => [...list, { id, message, tone, leaving: false }]);
    const fade = setTimeout(() => {
      setToasts((list) => list.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    }, duration);
    const remove = setTimeout(() => {
      timers.current.delete(id);
      setToasts((list) => list.filter((t) => t.id !== id));
    }, duration + REMOVE_AFTER_FADE_MS);
    timers.current.set(id, [fade, remove]);
  }, []);

  // Unmount: drop every pending timer so nothing sets state on a dead tree.
  useEffect(() => {
    const map = timers.current;
    return () => {
      for (const list of map.values()) for (const t of list) clearTimeout(t);
      map.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastZone toasts={toasts} />
    </ToastContext.Provider>
  );
}

export function ToastZone({ toasts }: { toasts: ToastItem[] }) {
  return (
    <div className="toast-zone" aria-live="polite" aria-atomic="true">
      {toasts.map((t) => (
        <div key={t.id} className={`toast toast--${t.tone}${t.leaving ? " toast--leaving" : ""}`}>
          {t.message}
        </div>
      ))}
    </div>
  );
}
